import { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useMotionValue,
    useSpring,
} from "framer-motion";
import { ArrowRight, Phone, Zap } from "lucide-react";
import { CONTACT, IMAGES, scrollToSection } from "@/lib/site";

const LINES = [
    { text: "Specialistische crisiszorg", accent: false },
    { text: "voor jeugd en LVB,", accent: false },
    { text: "met rust en aandacht.", accent: true },
];

export default function Hero() {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

    const rawX = useMotionValue(0);
    const rawY = useMotionValue(0);
    const rotateX = useSpring(rawX, { stiffness: 120, damping: 18 });
    const rotateY = useSpring(rawY, { stiffness: 120, damping: 18 });

    const onMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        rawY.set(px * 10);
        rawX.set(-py * 10);
    };
    const onLeave = () => {
        rawX.set(0);
        rawY.set(0);
    };

    return (
        <section
            id="top"
            ref={sectionRef}
            className="relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28"
        >
            <div
                className="pointer-events-none absolute -top-32 right-[-10%] h-[560px] w-[560px] rounded-full opacity-60"
                style={{ background: "radial-gradient(circle, rgba(200,90,50,0.16) 0%, rgba(200,90,50,0) 65%)" }}
            />
            <div
                className="pointer-events-none absolute bottom-[-20%] left-[-12%] h-[480px] w-[480px] rounded-full opacity-50"
                style={{ background: "radial-gradient(circle, rgba(61,90,69,0.14) 0%, rgba(61,90,69,0) 65%)" }}
            />

            <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-12 lg:gap-10 lg:px-8">
                <motion.div style={{ y: textY }} className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mb-7 inline-flex items-center gap-2 rounded-full border border-line bg-white/70 px-4 py-2"
                    >
                        <span className="h-2 w-2 rounded-full bg-terracotta" />
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-moss-800">
                            Zelfstandig begeleider (ZZP) • Jeugd &amp; LVB • Landelijk inzetbaar
                        </span>
                    </motion.div>

                    <h1
                        data-testid="hero-headline"
                        className="font-serif text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
                    >
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.accent ? "italic text-terracotta" : ""}`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        duration: 0.9,
                                        delay: 0.15 + i * 0.16,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        data-testid="hero-subhead"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.75 }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-moss-900/70 sm:text-lg"
                    >
                        Als ervaren zelfstandig begeleider ondersteun ik jongeren,
                        gezinnen en mensen met een licht verstandelijke beperking (LVB) in
                        acute en dreigende crisissituaties. Ik breng rust, structuur en
                        veiligheid terug — thuis of op locatie.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.9 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            onClick={() => scrollToSection("#intake")}
                            data-testid="hero-btn-primary-intake"
                            className="group flex items-center gap-2 rounded-full bg-terracotta px-7 py-4 text-sm font-semibold text-sand-50 transition-all duration-300 hover:-translate-y-0.5 hover:bg-terracotta-dark hover:shadow-xl"
                        >
                            Plan een gratis intakegesprek
                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <a
                            href={CONTACT.phoneHref}
                            data-testid="hero-btn-secondary-contact"
                            className="flex items-center gap-2 rounded-full border border-moss-900/15 bg-white/60 px-7 py-4 text-sm font-semibold text-moss-900 transition-all duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
                        >
                            <Phone size={16} />
                            Acute hulp? Bel direct
                        </a>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                        className="mt-8 text-xs font-medium uppercase tracking-[0.2em] text-moss-900/50"
                    >
                        Snel inzetbaar • Gemeenten, Wmo &amp; instellingen • Jeugdwet
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 1.25 }}
                        className="mt-10 flex max-w-md items-center gap-4 rounded-2xl border border-line bg-white/70 p-4 backdrop-blur"
                    >
                        <img
                            src={IMAGES.about}
                            alt="Begeleider Tuncay"
                            data-testid="hero-begeleider-avatar"
                            className="h-14 w-14 shrink-0 rounded-full border-2 border-terracotta/30 object-cover"
                        />
                        <p className="text-sm leading-snug text-moss-900/75">
                            <span className="block font-serif text-lg font-semibold text-moss-900">
                                Ik ben Tuncay, jouw vaste begeleider.
                            </span>
                            Eén gezicht, één aanspreekpunt — ook als het er echt toe doet.
                        </p>
                    </motion.div>
                </motion.div>

                <div className="lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        onMouseMove={onMove}
                        onMouseLeave={onLeave}
                        style={{ rotateX, rotateY, transformPerspective: 900 }}
                        className="relative"
                    >
                        <div className="absolute -inset-4 -z-10 rounded-t-[13rem] rounded-b-[2rem] bg-moss-800/10" />
                        <div className="overflow-hidden rounded-t-[12rem] rounded-b-[2rem] border border-line bg-white shadow-2xl">
                            <motion.img
                                src={IMAGES.hero}
                                alt="Rustige begeleiding in een crisissituatie"
                                data-testid="hero-care-image"
                                style={{ y: imgY }}
                                className="h-[420px] w-full scale-110 object-cover sm:h-[500px]"
                            />
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 1.1 }}
                            className="absolute -left-4 bottom-8 flex items-center gap-3 rounded-2xl border border-line bg-white/95 px-4 py-3 shadow-xl backdrop-blur sm:-left-8"
                        >
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-moss-100 text-moss-800">
                                <Zap size={18} />
                            </span>
                            <span className="text-sm font-semibold leading-tight">
                                Snel inzetbaar
                                <span className="block text-xs font-medium text-moss-900/60">ook bij een acute crisis</span>
                            </span>
                        </motion.div>

                        <motion.img
                            src={IMAGES.heroSecondary}
                            alt="Persoonlijk gesprek met crisisbegeleider"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 1.25 }}
                            className="absolute -right-4 -top-8 hidden h-28 w-28 rounded-full border-4 border-sand-50 object-cover shadow-xl sm:block"
                        />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
